import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatDate } from '../../../utils/formatDate';
import { useGet } from '../../../sharedModules/hooks/useQuery';
import { formatReportValue } from '../utils/formatReportValue';
import type { ApiInspectionReport, ApiHvacSystem, ApiPlumbingSystem } from '../utils/mapApiResponseToFormValues';

interface PreviousInspectionModalProps {
  inspectionId: number | null;
  open: boolean;
  onClose: () => void;
  onPrefill: (data: ApiInspectionReport) => void;
  /** Only the most recent previous report (per customer history) can pre-fill the form */
  prefillEnabled: boolean;
}

const sectionHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1,
  mt: 2,
};

/** Single label + value row */
function Field({ label, value }: { label: string; value: unknown }) {
  const { display, color: valueColor } = formatReportValue(value);

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={valueColor ? { color: valueColor, fontWeight: 600 } : undefined}>
        {display}
      </Typography>
    </Box>
  );
}

function HvacSystemSection({ sys, index }: { sys: ApiHvacSystem; index: number }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ ...sectionHeaderSx, mt: index === 0 ? 0 : 2 }}>
        HVAC {index + 1} {sys.location ? `— ${sys.location}` : ''}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={3}>
          <Field label="Manufacturer" value={sys.manufacturer} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Age" value={sys.age} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Age Condition" value={sys.age_condition} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Visual Condition" value={sys.overall_visual_condition} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Overall Operation" value={sys.overall_operation} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Proper Clearance" value={sys.proper_clearance} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Service Record Updated" value={sys.service_record_updated} />
        </Grid>
      </Grid>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Type
        </Typography>
        <Typography variant="body2">
          {sys.system_type ? sys.system_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'}
        </Typography>
      </Box>
      {sys.system_type === 'boiler' && (
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3}>
            <Field label="A/C Present" value={sys.boiler_ac_present} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Expansion Tank" value={sys.boiler_expansion_tank_condition} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Heat Exchanger" value={sys.boiler_heat_exchanger_condition} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Pressure Relief" value={sys.boiler_pressure_relief_condition} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Auto-Fill" value={sys.boiler_auto_fill_condition} />
          </Grid>
        </Grid>
      )}
      {sys.notes && <Field label="Notes" value={sys.notes} />}
    </Box>
  );
}

function PlumbingSystemSection({ sys, index }: { sys: ApiPlumbingSystem; index: number }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ ...sectionHeaderSx, mt: index === 0 ? 0 : 2 }}>
        Plumbing System {index + 1} {sys.location ? `— ${sys.location}` : ''}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={3}>
          <Field label="Manufacturer" value={sys.manufacturer} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Age" value={sys.age} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Visual Condition" value={sys.overall_visual_condition} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Expansion Tank" value={sys.expansion_tank_condition} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Visible Leaks or Rust" value={sys.visible_leaks_or_rust} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field label="Proper Clearance" value={sys.proper_clearance} />
        </Grid>
        {sys.system_type === 'tankless_water_heater' && (
          <>
            <Grid item xs={6} sm={3}>
              <Field label="Annual Maintenance" value={sys.recommended_annual_maintenance} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Field label="$50 Coupon Installed" value={sys.installed_coupon} />
            </Grid>
          </>
        )}
      </Grid>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Type
        </Typography>
        <Typography variant="body2">
          {sys.system_type ? sys.system_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'}
        </Typography>
      </Box>
      {sys.notes && <Field label="Notes" value={sys.notes} />}
    </Box>
  );
}

function PreviousInspectionModal({
  inspectionId,
  open,
  onClose,
  onPrefill,
  prefillEnabled,
}: PreviousInspectionModalProps) {
  const { data, isLoading, error, refetch } = useGet<ApiInspectionReport>({
    url: `/home_services/inspection_reports/${inspectionId}`,
    enabled: open && !!inspectionId,
  });

  const report = data?.data ?? null;

  const handlePrefill = () => {
    if (report) {
      onPrefill(report);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ bgcolor: '#3d5a80', color: '#fff', py: 1.5 }}>
        Past Home Inspection Report
        {report && (
          <Typography component="span" sx={{ color: '#cdd8e3', ml: 1, fontSize: '14px' }}>
            {formatDate(report.created_at)} · #{report.order_no}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !isLoading && (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load this inspection report.
          </Alert>
        )}

        {report && (
          <>
            {/* Header summary */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Field label="Customer" value={report.name} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Technician" value={report.technician} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Date" value={formatDate(report.date)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Offered Maintenance Plan" value={report.offered_maintenance_plan} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* HVAC Systems */}
            {report.hvac_systems?.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 600, color: '#3d5a80', mb: 1 }}>HVAC</Typography>
                {report.hvac_systems.map((sys, i) => (
                  <HvacSystemSection key={sys.id} sys={sys} index={i} />
                ))}
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Indoor Air Quality */}
            {report.air_quality &&
              (() => {
                const aq = report.air_quality as Record<string, unknown>;
                const notes = aq.notes ?? aq.air_quality_notes ?? aq.airQualityNotes;
                return (
                  <>
                    <Typography sx={{ fontWeight: 600, color: '#3d5a80', mb: 1 }}>Indoor Air Quality</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Field label="Air Filter Condition" value={aq.air_filter_condition} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Humidifier Status" value={aq.humidifier_status} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Dehumidifier Status" value={aq.dehumidifier_status} />
                      </Grid>
                    </Grid>
                    {notes && <Field label="Notes" value={notes} />}
                    <Divider sx={{ my: 2 }} />
                  </>
                );
              })()}

            {/* Plumbing Systems */}
            {report.plumbing_systems?.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 600, color: '#3d5a80', mb: 1 }}>Plumbing Systems</Typography>
                {report.plumbing_systems.map((sys, i) => (
                  <PlumbingSystemSection key={sys.id} sys={sys} index={i} />
                ))}
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Electrical */}
            {report.electrical &&
              (() => {
                const el = report.electrical as Record<string, unknown>;
                return (
                  <>
                    <Typography sx={{ fontWeight: 600, color: '#3d5a80', mb: 1 }}>Electrical</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Field label="Manufacturer" value={el.manufacturer} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Visual Condition" value={el.overall_visual_condition} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Surge Protection" value={el.whole_house_surge_protection} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Wiring" value={el.wiring_secured_properly_terminated} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Generator" value={el.whole_home_generator_condition} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Field label="Proper Clearance" value={el.proper_clearance} />
                      </Grid>
                    </Grid>
                    {el.notes && <Field label="Notes" value={el.notes} />}
                    <Divider sx={{ my: 2 }} />
                  </>
                );
              })()}

            {/* Water Treatment */}
            {(() => {
              const wt = report.water_treatment as Record<string, unknown>;
              if (!wt || wt.n_a === 'true' || wt.n_a === true) return null;
              return (
                <>
                  <Typography sx={{ fontWeight: 600, color: '#3d5a80', mb: 1 }}>Water Treatment</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Field label="Manufacturer" value={wt.manufacturer} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Field label="Visible Condition" value={wt.visible_condition} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Field label="Annual Maintenance" value={wt.recommended_annual_maintenance} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Field label="Filters/Salt Required" value={wt.filters_or_salt_required} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Field label="Water Quality Complaints" value={wt.water_quality_complaints} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Field label="Location For Solution" value={wt.location_for_solution} />
                    </Grid>
                  </Grid>
                  {wt.notes && <Field label="Notes" value={wt.notes} />}
                </>
              );
            })()}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {report?.pdf_url && (
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={report.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View PDF
          </Button>
        )}
        {prefillEnabled && (
          <Button
            variant="contained"
            onClick={handlePrefill}
            disabled={!report}
            sx={{ bgcolor: '#3d5a80', '&:hover': { bgcolor: '#2c4460' } }}
          >
            Use This Data
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PreviousInspectionModal;
